import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Cpu, Send, Zap, Loader, Radio, CheckSquare, Search, Play, FileText, ArrowRight, Check, AlertCircle } from 'lucide-react';
import { NodeAgent, NexusUser } from '../types/nexus';

interface NexusAgentsProps {
  user: NexusUser;
  onAddLog: (module: string, action: string, status: 'SUCCESS' | 'WARNING' | 'DENIED' | 'PENDING_APPROVAL', details: string) => void;
  onDeductTasks: (amount: number) => void;
}

export const NexusAgents: React.FC<NexusAgentsProps> = ({
  user,
  onAddLog,
  onDeductTasks
}) => {
  const [agents, setAgents] = useState<NodeAgent[]>([
    { id: 'ag-01', name: 'Vanguard Search Agent', role: 'Search the web through Nexus Search indexes.', status: 'Idle', creditsPerTask: 1 },
    { id: 'ag-02', name: 'Lead Research Agent', role: 'Dossier competitor analysis & output Outreach copy.', status: 'Idle', creditsPerTask: 2 },
    { id: 'ag-03', name: 'Hosting Support Watcher Agent', role: 'Perform health checkups & auto restart proxy router.', status: 'Executing', currentTask: 'Pinging Hetzner bare-metal container metrics...', creditsPerTask: 1 },
    { id: 'ag-04', name: 'Campaign Ad Factory Agent', role: 'Generate ad scripts & feed visual briefs to Media Studio.', status: 'Idle', creditsPerTask: 3 }
  ]);

  const [selectedAgentId, setSelectedAgentId] = useState('ag-01');
  const [taskPrompt, setTaskPrompt] = useState('');
  const [isExecuting, setIsExecuting] = useState(false);
  const [agentLogs, setAgentLogs] = useState<string[]>([
    "[AG-03] PING SUCCEEDED to 188.40.168.109 - 0.04ms latency",
    "[SYSTEM] All nodes idle. Awaiting user-queued dispatch triggers..."
  ]);

  const pipelines = [
    {
      id: 'pipe-1',
      name: 'Search to Lead Pipeline',
      flow: 'Nexus Search → Lead Research Agent → WorkSuite CRM → Outreach Draft → Approval → Client Workspace',
      desc: 'Searches for regional prospects, analyzes contact routes, drafts B2B pitches, and pipelines approved tasks.',
      status: 'Ready'
    },
    {
      id: 'pipe-2',
      name: 'Search to Product Comparison',
      flow: 'Nexus Search → Product Search Agent → Supplier Shortlist → Report → Client Approval',
      desc: 'Retrieves pricing matrix datasets, matches specifications, isolates gaps, and exports direct order sheets.',
      status: 'Ready'
    },
    {
      id: 'pipe-3',
      name: 'Search to Ad Campaign Pipeline',
      flow: 'Nexus Search → Campaign Agent → AI Ad Factory → Media Studio → Social Automation → Analytics',
      desc: 'Detects trending industry terms, crafts script hooks, renders graphic assets, and queues posts.',
      status: 'Ready font-bold animate-pulse'
    },
    {
      id: 'pipe-4',
      name: 'Hosting to Support Ticket',
      flow: 'Nexus Hosting → Hosting Support Agent → WorkSuite Ticket → Client Update → Admin Escalation',
      desc: 'Monitors Nginx reverse proxy load states, signals warnings instantly into WorkSuite, and emails support team.',
      status: 'Active Monitor'
    }
  ];

  const handleRunAgent = (e: React.FormEvent) => {
    e.preventDefault();
    if (!taskPrompt.trim()) return;

    const targetAgent = agents.find(a => a.id === selectedAgentId);
    if (!targetAgent) return;

    if (user.aiTasksRemaining < targetAgent.creditsPerTask) {
      onAddLog('Node Agent Core', `Trigger: "${targetAgent.name}"`, 'DENIED', 'Insufficent task credits for agent run.');
      alert('Insufficient AI Agent tasks remaining! Please top up your subscription.');
      return;
    }

    setIsExecuting(true);
    onAddLog('Node Agent Core', `Trigger Agent: "${targetAgent.name}" - query: "${taskPrompt}"`, 'SUCCESS', 'Agent thread spawned successfully.');

    // Append logs
    setAgentLogs(prev => [
      `[PROCESS START] Spawming localized node threads on AMD cluster...`,
      `[PARAM CAPTURED] prompt_query: "${taskPrompt.toUpperCase()}"`,
      ...prev
    ]);

    setTimeout(() => {
      setIsExecuting(false);
      onDeductTasks(targetAgent.creditsPerTask);

      // Mutate agent
      setAgents(prev => prev.map(a => a.id === selectedAgentId ? { ...a, status: 'Idle' } : a));

      setAgentLogs(prev => [
        `[SUCCESS] "${targetAgent.name}" completed target tasks. ${targetAgent.creditsPerTask} Task credits deducted.`,
        `[OUTPUT LOG] Generated project brief successfully populated inside WorkSuite database!`,
        ...prev
      ]);

      setTaskPrompt('');
      onAddLog('Node Agent Core', `Agent execution successful: "${targetAgent.name}"`, 'SUCCESS', `Task executed. Deducted ${targetAgent.creditsPerTask} credits.`);
      alert('Agent finished execution! Check the Activity Log panel for outputs.');
    }, 2500);
  };

  const handleTriggerPipeline = (name: string) => {
    onAddLog('Automation Pipeline', `Trigger pipeline: "${name}"`, 'SUCCESS', 'Custom multi-step automation workflow initialized.');
    alert(`Automation Pipeline "${name}" triggered!\n\nAll constituent stages (search algorithms, content generators, social publication) are synching securely on background threads.`);
  };

  return (
    <div className="w-full space-y-6">
      {/* Banner */}
      <div className="glass-panel border border-[#00ffff]/20 p-6 rounded-2xl bg-gradient-to-r from-purple-950/10 via-black/40 to-cyan-950/10">
        <h2 className="text-2xl font-sans font-black tracking-tight text-white uppercase flex items-center gap-2">
          <Cpu className="w-6 h-6 text-[#00ffff] animate-pulse" />
          Nexus Node Agent
        </h2>
        <p className="text-xs font-mono text-cyan-405/80 uppercase tracking-widest mt-1">
          NexoraX Autonomous Business Automation Engines
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left main interactive agent manager */}
        <div className="lg:col-span-2 space-y-6">
          {/* Active Agents list */}
          <div className="space-y-3">
            <h3 className="text-xs font-mono font-black text-slate-400 uppercase tracking-widest pl-1">
              Select Agent Node Deployed
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {agents.map((ag) => (
                <div
                  key={ag.id}
                  onClick={() => setSelectedAgentId(ag.id)}
                  className={`glass-panel border p-4 rounded-xl cursor-pointer transition-all flex flex-col justify-between ${
                    selectedAgentId === ag.id
                      ? 'border-[#00ffff]/40 bg-cyan-950/10 shadow-[0_0_12px_rgba(0,255,255,0.06)]'
                      : 'border-zinc-850 bg-black/40 hover:border-zinc-800'
                  }`}
                >
                  <div className="space-y-1">
                    <div className="flex justify-between items-center border-b border-zinc-900 pb-1.5 mb-1.5 text-[8px] font-mono">
                      <span className="text-[#00ffff] font-black uppercase">CREDITS / USE: {ag.creditsPerTask}</span>
                      <span className={`px-1 rounded ${
                        ag.status === 'Executing' ? 'bg-amber-950/40 text-amber-500 animate-pulse' : 'bg-zinc-800 text-slate-400'
                      }`}>
                        {ag.status}
                      </span>
                    </div>
                    <h4 className="text-xs font-sans font-black uppercase text-white tracking-tight">
                      {ag.name}
                    </h4>
                    <p className="text-[10px] text-slate-400 leading-normal normal-case font-medium pt-1">
                      {ag.role}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Prompt Executor Box */}
          <div className="glass-panel border border-zinc-850 p-5 rounded-2xl bg-black/40">
            <form onSubmit={handleRunAgent} className="space-y-4">
              <h3 className="text-xs font-mono font-black text-white uppercase tracking-wider">
                Assign Command Task Target
              </h3>

              <div className="relative">
                <input
                  type="text"
                  placeholder="Task instructions (e.g. Find 10 regional takeaway leads in Dunfermline & draft outreach)..."
                  value={taskPrompt}
                  onChange={(e) => setTaskPrompt(e.target.value)}
                  className="w-full bg-black/50 border border-zinc-800 rounded-xl px-4 py-3 text-xs font-mono text-white tracking-wide outline-none focus:border-cyan-400/50 uppercase"
                />
              </div>

              <button
                type="submit"
                disabled={isExecuting || !taskPrompt.trim()}
                className="w-full py-2.5 bg-gradient-to-r from-purple-600 to-[#00ffff] text-black font-mono text-xs font-black uppercase tracking-wider rounded-xl transition-all shadow-[0_0_10px_rgba(0,255,255,0.15)] flex items-center justify-center gap-2 cursor-pointer"
              >
                {isExecuting ? 'SPANNING WORKER CORE THREADS IN CLUSTER...' : 'DEPLOY ACTIVE AGENT'}
              </button>
            </form>
          </div>

          {/* Activity Log Dashboard */}
          <div className="glass-panel border border-zinc-850 p-5 rounded-2xl bg-black/40 space-y-3 font-mono text-[10px]">
            <div className="flex justify-between items-center border-b border-zinc-900 pb-2">
              <span className="text-slate-400 font-black uppercase tracking-wider">
                Autonomous Node Activity Logs (Chronos ledger stream)
              </span>
              <button
                onClick={() => {
                  setAgentLogs([`[CLEAR] Telemetry stream flushed by systems operator.`, `[SYSTEM] All threads ready.`]);
                }}
                className="text-slate-500 hover:text-white text-[9px]"
              >
                Flush Logs
              </button>
            </div>
            <div className="h-44 bg-zinc-950/80 rounded-lg p-3 overflow-y-auto space-y-1.5 text-[8.5px] border border-zinc-900 text-zinc-400 custom-scrollbar leading-relaxed">
              {agentLogs.map((log, index) => (
                <div key={index} className="border-l-2 border-cyan-500/20 pl-2">
                  {log}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right column visible visual pipelines flow cards (1/3 col) */}
        <div className="space-y-6">
          <div className="glass-panel border border-zinc-800 p-5 rounded-2xl bg-black/30 space-y-4">
            <h3 className="text-xs font-sans font-black text-white uppercase tracking-widest border-b border-zinc-900 pb-2 flex items-center gap-1.5">
              <Zap className="w-4 h-4 text-[#00ffff]" />
              Active Automation Pipelines
            </h3>

            <div className="space-y-4 text-[10px] font-mono">
              {pipelines.map((pipe) => (
                <div key={pipe.id} className="p-3 bg-black/50 border border-zinc-900 rounded-xl space-y-2 relative group hover:border-[#00ffff]/20 transition-all">
                  <div className="flex justify-between items-center">
                    <span className="text-white font-extrabold">{pipe.name}</span>
                    <span className="px-2 py-0.5 rounded bg-cyan-950/40 text-[#00ffff] border border-cyan-500/10 text-[8px] uppercase">
                      {pipe.status}
                    </span>
                  </div>

                  <p className="text-[9px] text-slate-400 normal-case leading-tight font-sans font-medium">
                    {pipe.desc}
                  </p>

                  <div className="bg-zinc-950 p-2.5 rounded text-[8px] text-[#00ffff]/70 break-all leading-relaxed whitespace-pre-wrap">
                    {pipe.flow}
                  </div>

                  <button
                    onClick={() => handleTriggerPipeline(pipe.name)}
                    className="w-full py-1 bg-cyan-950/40 hover:bg-[#00ffff] hover:text-black border border-cyan-500/15 text-[#00ffff] font-mono text-[8px] uppercase font-bold rounded transition-colors cursor-pointer"
                  >
                    🚀 Trigger Pipeline
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
